/**
 * createMarketingApp — one-line factory for an app's marketing-website
 * entry point. Returns a configured React component that the app's
 * `main.jsx` renders into the DOM root.
 *
 * Replaces the ~50–80 LOC of provider stack + router setup + chrome
 * composition that each app re-implements today. The factory bundles:
 *   - <BrowserRouter>
 *   - <I18nextProvider> (when i18n is passed), with <MarketingLanguage> — `<html lang>` and each
 *     page's WebPage JSON-LD name the language the page shows (WCAG 3.1.1, F-Q7-2)
 *   - <CookieConsentProvider> (REQUIRED — AnalyticsProvider depends on it)
 *   - <AnalyticsProvider>
 *   - <ManifestProvider>
 *   - <MarketingShell> with header + footer
 *   - <Routes> built from the `routes` array
 *
 * Each route's `element` is rendered as the page body inside <MarketingShell>'s
 * <main>. Pages own their own H1 (typically via <MarketingHero>); the shell
 * provides everything around the page content.
 *
 * A route either carries a code-authored page `element`, or names one of the
 * four blog templates (`routes[i].template`, see `BLOG_TEMPLATE_ROUTES`), which
 * <BlogTemplateRoute> renders from the configured `cmsClient`. The CMS-driven
 * page/section builder that once rendered other templates was pruned
 * (LIFE-SHORTLISTED-001 Q-LS-12): the product sites are code-authored.
 *
 * @module @puffin-core/marketing-shell/createMarketingApp
 */

import { useMemo } from 'react';
import { BrowserRouter, StaticRouter, Routes, Route } from 'react-router-dom';
import { I18nextProvider, useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import MarketingShell from '../components/MarketingShell.jsx';
import { MarketingLanguage } from '../i18n/MarketingLanguage.jsx';
import ManifestProvider from '../components/ManifestProvider.jsx';
import { CookieConsentProvider } from '../components/CookieConsent.jsx';
import AnalyticsProvider from '../components/AnalyticsProvider.jsx';
import BlogTemplateRoute from '../blog/BlogTemplateRoute.jsx';
import { withLocalePrefix } from '../blog/blogLocalePath.js';
import buildShellProps from './buildShellProps.jsx';
import validatePrivacyHref from './validatePrivacyHref.js';

const REQUIRED_CMS_CLIENT_METHODS = Object.freeze([
  'listBlogPosts',
  'resolveBlogPost',
  'listBlogCategories',
  'listBlogTags',
]);

/** What the locale switcher calls on the site's StorageService instance. */
const CHROME_STORAGE_METHODS = Object.freeze(['get', 'set']);

/** What the cookie-consent provider calls on the site's StorageService instance. */
const CONSENT_STORAGE_METHODS = Object.freeze(['isStorageAvailable', 'getJSON', 'setJSON']);

/**
 * Refuse, by name, anything but a StorageService INSTANCE carrying the methods its reader calls
 * (N198 batch 6: the site builds ONE instance with its logger; no process-wide singleton). The
 * class itself is a function, so it is refused too.
 *
 * @param {*} value
 * @param {string} path - the config path, for the message.
 * @param {readonly string[]} methods
 * @returns {void}
 */
function requireStorageInstance(value, path, methods) {
  if (
    !value ||
    typeof value !== 'object' ||
    methods.some((method) => typeof value[method] !== 'function')
  ) {
    throw new TypeError(
      `createMarketingApp: ${path} is required: the site's ONE StorageService instance ` +
        `(with ${methods.join(' / ')}), never the StorageService class.`,
    );
  }
}

/**
 * @typedef {Object} RouteConfig
 * @property {string} path                React Router path.
 * @property {React.ReactNode} [element]  The code-authored page component.
 * @property {string} [template]          One of the blog templates
 *                                        (`BlogTemplateRoute.BLOG_TEMPLATES`). When provided,
 *                                        the route is rendered via <BlogTemplateRoute>
 *                                        + the configured cmsClient.
 *
 * Exactly one of `element` | `template` MUST be set per route. Routes that
 * declare both raise a configuration error at construction.
 */

/**
 * @typedef {Object} CookieConsentConfig
 * @property {string} storageKey
 * @property {Object} storage            the site's ONE StorageService instance (never the class)
 * @property {string} STORAGE_TYPE_LOCAL
 * @property {string} privacyHref        REQUIRED, no default. The site's OWN privacy-policy route —
 *                                        the portal passes its route registry's `PRIVACY` constant
 *                                        (e.g. `PLANNER_MARKETING_ROUTES.PRIVACY`), the same path its
 *                                        `routes` table declares. The banner is the ONE link an
 *                                        unauthenticated visitor needs before consenting, and the
 *                                        factory used to drop it, so every Colony marketing site
 *                                        rendered `href={undefined}` (a link with no destination).
 * @property {Function} [onConsentChange]
 */

/**
 * @typedef {Object} CmsClientShape
 * @property {Function} listBlogPosts
 * @property {Function} resolveBlogPost
 * @property {Function} listBlogCategories
 * @property {Function} listBlogTags
 */

/**
 * @typedef {Object} AnalyticsConfig
 * @property {{track: (envelope) => void}} transport
 *   Transport adapter — receives `{type:'marketing.<appKey>.<event>', ...}` envelopes.
 * @property {{get: (key) => string|null, set: (key, value) => void}} storageAdapter
 *   Browser-storage adapter (localStorage shape). Backs UTM capture + session tagging.
 * @property {(error: Error) => void} onTransportError
 *   Required beside `transport`: forwarded to <AnalyticsProvider>, which reports every failure of
 *   the GA4 leg and composer it builds through it (a failure is reported, never swallowed).
 *
 * When omitted, AnalyticsProvider mounts in no-op mode (useAnalytics() works,
 * but events are silently dropped). Provide BOTH `transport` and
 * `storageAdapter` to enable live emit.
 */

/**
 * @typedef {Object} CreateMarketingAppConfig
 * @property {string} appKey                          brand-assets key.
 * @property {readonly RouteConfig[]} routes
 * @property {{descriptor: (args: {t: Function}) => Object, storage: Object}} chrome
 *   The site's chrome: its descriptor BUILDER (`buildMarketingChromeDescriptor`) and the site's
 *   StorageService instance the locale switcher persists through. The factory resolves the header, footer and skip
 *   link from it on EVERY render, through `buildShellProps`, in the language in force — so the
 *   chrome follows a language switch, and hydrates in the language the document was rendered in
 *   (Q-LC24-7). Built once at module load, it did neither.
 * @property {Object} [i18n]                          react-i18next instance.
 * @property {CookieConsentConfig} cookieConsent      Cookie-consent provider config (required —
 *                                                    AnalyticsProvider gates tracking on consent
 *                                                    context, so every marketing-website needs
 *                                                    a real cookie-consent provider in the tree).
 * @property {CmsClientShape} [cmsClient]             Required if any route has a blog
 *                                                    template (one of `blog-index`,
 *                                                    `blog-post`, `blog-category`, `blog-tag`).
 *                                                    Typically built via
 *                                                    `createMarketingCmsClient` from this
 *                                                    package.
 * @property {string} [defaultLocale='en']            Locale used when no i18n instance is
 *                                                    available (e.g. before the i18n provider
 *                                                    boots).
 * @property {AnalyticsConfig} [analytics]            Optional analytics wiring. When supplied,
 *                                                    `appKey` + `analytics.transport` +
 *                                                    `analytics.storageAdapter` are threaded
 *                                                    into <AnalyticsProvider>, which then emits
 *                                                    `marketing.<appKey>.<event>` envelopes via
 *                                                    the transport (consent-gated). Omit to run
 *                                                    in no-op mode.
 */

/**
 * Build the marketing-website root component.
 *
 * @param {CreateMarketingAppConfig} config
 * @returns {React.ComponentType} A component ready for `createRoot().render(<App />)`.
 */
export default function createMarketingApp(config) {
  validateConfig(config);

  const {
    appKey,
    routes,
    chrome,
    i18n,
    cookieConsent,
    cmsClient,
    defaultLocale = 'en',
    analytics,
    staticLocation,
  } = config;

  const analyticsTransport = analytics?.transport;
  const analyticsStorageAdapter = analytics?.storageAdapter;
  const analyticsOnTransportError = analytics?.onTransportError;

  /**
   * The chrome, resolved from the language in force. `useTranslation` subscribes to
   * `languageChanged`, so a language switch re-renders it — and during hydration the mount holds the
   * language to the document's (`mountMarketingRoot`), so the first render matches the server's.
   *
   * @returns {{headerProps: Object, footerProps: Object}}
   */
  function useChromeProps() {
    const { t, i18n: t18n } = useTranslation();
    const locale = t18n && t18n.language ? t18n.language : defaultLocale;
    return useMemo(
      () =>
        buildShellProps({
          descriptor: chrome.descriptor({ t }),
          locale,
          // The locale that owns the unprefixed blog URL is the factory's, so the switcher's blog
          // rewrite (`/<locale>/blog/…`) agrees with the variants registered below.
          defaultLocale,
          storage: chrome.storage,
        }),
      [t, locale],
    );
  }

  function BlogTemplateRouteWrapper({ template, forcedLocale }) {
    // `forcedLocale` is set on the locale-prefixed route variants (e.g. the
    // `/es/blog` route passes forcedLocale='es') and pins the CONTENT locale so
    // the index/post fetch native rows for that language. The unprefixed
    // `/blog` route leaves it undefined and takes the locale from i18n, falling
    // back to the factory-level defaultLocale. We deliberately do NOT call
    // `i18n.changeLanguage` here: the LocaleSwitcher owns the app-language +
    // persistence, and syncing it from the route would fight the switcher's
    // own changeLanguage effect into an infinite render loop. Chrome language
    // therefore follows the switcher (which itself rewrites to `/es/blog` on
    // selection — the primary flow that arrives here already in `es`).
    //
    // `useTranslation()` never throws when no provider is present: it falls back
    // to `getI18n()`, and with no global instance either it warns once and
    // returns `i18n: {}` (react-i18next@17 `useTranslation.js:34-38,110`). The
    // `t18n.language` guard below is what resolves that case to `defaultLocale`.
    // It must stay a real hook call rather than `useContext(I18nContext)`: only
    // `useTranslation` subscribes to `languageChanged`, so reading the context
    // directly would freeze the content locale.
    let locale = forcedLocale || defaultLocale;
    const { i18n: t18n } = useTranslation();
    if (!forcedLocale && t18n && t18n.language) {
      locale = t18n.language;
    }
    // Locale-prefixed routes link onward in-prefix (`/es/blog/<slug>`) so post +
    // category + tag navigation stays in the same locale namespace (and those
    // targets are themselves registered locale-prefixed routes). The unprefixed
    // route keeps the canonical `/blog` href base.
    const hrefPrefix = forcedLocale
      ? withLocalePrefix('/blog', forcedLocale, defaultLocale)
      : '/blog';
    return (
      <BlogTemplateRoute
        template={template}
        cmsClient={cmsClient}
        locale={locale}
        hrefPrefix={hrefPrefix}
      />
    );
  }
  BlogTemplateRouteWrapper.propTypes = {
    template: PropTypes.string.isRequired,
    forcedLocale: PropTypes.string,
  };

  function MarketingAppContents() {
    const { headerProps, footerProps } = useChromeProps();
    // MLC-001 WS3 — the LocaleSwitcher rewrites blog URLs to `/<locale>/blog` on
    // switch (the canonical shareable + hreflang form, matching the SSR serve
    // path). Register a locale-prefixed route variant for every non-default
    // locale the switcher offers so those URLs render the index in-language
    // instead of falling through to NotFound. The switcher's languages live on
    // `headerProps.localeProps.languages` (the shape buildShellProps emits + the
    // one MarketingHeader consumes). Apps without a locale switcher get exactly
    // the prior (single, unprefixed) blog routes.
    const prefixableLocales = (
      Array.isArray(headerProps?.localeProps?.languages) ? headerProps.localeProps.languages : []
    )
      .map((lang) => (lang && typeof lang.code === 'string' ? lang.code : null))
      .filter((code) => code && code !== defaultLocale);
    return (
      <ManifestProvider>
        <AnalyticsProvider
          appKey={appKey}
          transport={analyticsTransport}
          storageAdapter={analyticsStorageAdapter}
          onTransportError={analyticsOnTransportError}
        >
          <MarketingShell appKey={appKey} headerProps={headerProps} footerProps={footerProps}>
            <Routes>
              {routes.flatMap((route) => {
                if (route.template) {
                  // Canonical (default-locale, unprefixed) route + one
                  // locale-prefixed variant per switcher-offered non-default
                  // locale, all bound to the same wrapper.
                  const nodes = [
                    <Route
                      key={route.path}
                      path={route.path}
                      element={<BlogTemplateRouteWrapper template={route.template} />}
                    />,
                  ];
                  for (const code of prefixableLocales) {
                    const localePath = withLocalePrefix(route.path, code, defaultLocale);
                    nodes.push(
                      <Route
                        key={localePath}
                        path={localePath}
                        element={
                          <BlogTemplateRouteWrapper template={route.template} forcedLocale={code} />
                        }
                      />,
                    );
                  }
                  return nodes;
                }
                return [<Route key={route.path} path={route.path} element={route.element} />];
              })}
            </Routes>
          </MarketingShell>
        </AnalyticsProvider>
      </ManifestProvider>
    );
  }

  function MarketingApp() {
    const tree = (
      <CookieConsentProvider
        storageKey={cookieConsent.storageKey}
        storage={cookieConsent.storage}
        STORAGE_TYPE_LOCAL={cookieConsent.STORAGE_TYPE_LOCAL}
        onConsentChange={cookieConsent.onConsentChange}
        privacyHref={cookieConsent.privacyHref}
      >
        <MarketingAppContents />
      </CookieConsentProvider>
    );
    const wrappedI18n = i18n ? (
      <I18nextProvider i18n={i18n}>
        <MarketingLanguage i18n={i18n}>{tree}</MarketingLanguage>
      </I18nextProvider>
    ) : (
      tree
    );
    // SSG seam (MSSG-001) — when `staticLocation` is supplied (build-time
    // prerender via react-dom/server), render under a `StaticRouter` at that
    // path instead of the browser-history router. Every provider in the stack
    // is already SSR-safe (AnalyticsProvider no-op mode, CookieConsentProvider
    // deterministic init), so the same tree renders identically server + client
    // and the SPA hydrates the prerendered markup with no mismatch. Omitting
    // `staticLocation` (the default for every runtime mount) is unchanged.
    if (typeof staticLocation === 'string') {
      return <StaticRouter location={staticLocation}>{wrappedI18n}</StaticRouter>;
    }
    return <BrowserRouter>{wrappedI18n}</BrowserRouter>;
  }

  return MarketingApp;
}

function validateConfig(config) {
  if (!config || typeof config !== 'object') {
    throw new TypeError('createMarketingApp: config object is required');
  }
  if (!config.appKey || typeof config.appKey !== 'string') {
    throw new TypeError('createMarketingApp: config.appKey (string) is required');
  }
  if (!Array.isArray(config.routes) || config.routes.length === 0) {
    throw new TypeError('createMarketingApp: config.routes (non-empty array) is required');
  }
  let anyBlogTemplateRoute = false;
  for (const r of config.routes) {
    if (!r.path || typeof r.path !== 'string') {
      throw new TypeError(
        `createMarketingApp: every route requires a string \`path\` (got ${JSON.stringify(r)})`,
      );
    }
    const hasElement = r.element !== undefined;
    const hasTemplate = typeof r.template === 'string' && r.template.length > 0;
    if (hasElement && hasTemplate) {
      throw new TypeError(
        `createMarketingApp: route "${r.path}" declares both \`element\` and \`template\` — pick exactly one.`,
      );
    }
    if (!hasElement && !hasTemplate) {
      throw new TypeError(
        `createMarketingApp: route "${r.path}" is missing both \`element\` and \`template\` — pick one.`,
      );
    }
    if (hasTemplate) {
      if (!BlogTemplateRoute.BLOG_TEMPLATES.includes(r.template)) {
        throw new TypeError(
          `createMarketingApp: route "${r.path}" template "${r.template}" is not a blog template ` +
            `(${BlogTemplateRoute.BLOG_TEMPLATES.join(', ')}). Every other page is a code-authored \`element\`.`,
        );
      }
      anyBlogTemplateRoute = true;
    }
  }
  if (anyBlogTemplateRoute) {
    if (!config.cmsClient || typeof config.cmsClient !== 'object') {
      throw new TypeError(
        'createMarketingApp: at least one route declares a blog template, so config.cmsClient ' +
          '(typically built via createMarketingCmsClient) is required.',
      );
    }
    for (const method of REQUIRED_CMS_CLIENT_METHODS) {
      if (typeof config.cmsClient[method] !== 'function') {
        throw new TypeError(
          `createMarketingApp: config.cmsClient is missing required method "${method}" (a blog template route is declared).`,
        );
      }
    }
  }
  if (!config.chrome || typeof config.chrome.descriptor !== 'function') {
    throw new TypeError('createMarketingApp: config.chrome.descriptor (function) is required');
  }
  requireStorageInstance(config.chrome.storage, 'config.chrome.storage', CHROME_STORAGE_METHODS);
  // Build the shell props once now, so a descriptor they cannot be built from fails at build time
  // (when the site's module loads) rather than on a visitor's first render.
  buildShellProps({
    descriptor: config.chrome.descriptor({
      t: config.i18n ? config.i18n.t.bind(config.i18n) : (key) => key,
    }),
    locale: config.defaultLocale || 'en',
  });
  if (!config.cookieConsent || typeof config.cookieConsent !== 'object') {
    throw new TypeError(
      'createMarketingApp: config.cookieConsent (object) is required — AnalyticsProvider needs ' +
        'a CookieConsentProvider in the tree to gate tracking on visitor consent. Pass ' +
        '{ storageKey, storage, STORAGE_TYPE_LOCAL, privacyHref }.',
    );
  }
  const cc = config.cookieConsent;
  if (typeof cc.storageKey !== 'string') {
    throw new TypeError('createMarketingApp: config.cookieConsent.storageKey (string) is required');
  }
  requireStorageInstance(cc.storage, 'config.cookieConsent.storage', CONSENT_STORAGE_METHODS);
  if (typeof cc.STORAGE_TYPE_LOCAL !== 'string') {
    throw new TypeError(
      'createMarketingApp: config.cookieConsent.STORAGE_TYPE_LOCAL (string) is required',
    );
  }
  // Required at the factory's own boundary, so a missing destination throws when the site's module
  // loads (build, prerender and test time), never on a visitor's render. The rule is shared with
  // createA11yPageHarness, which mounts the same provider stack for the axe tests.
  validatePrivacyHref(cc.privacyHref, 'createMarketingApp: config.cookieConsent.privacyHref');
  // ADMIN-PG-CLOSURE-001 / Phase 6 (2026-05-21): config.cookieConsent.t
  // is no longer required. CookieConsentBanner reads its translation
  // function via `useTranslation()` from react-i18next context. The
  // I18nextProvider mounted by the factory satisfies that dependency.
}

// Re-export PropTypes shapes for consumers building their own factory wrappers.
createMarketingApp.routePropTypes = PropTypes.arrayOf(
  PropTypes.shape({
    path: PropTypes.string.isRequired,
    element: PropTypes.node,
    template: PropTypes.string,
  }),
);
